import {
  CREATE_CHAT_SESSION,
  UPDATE_CHAT_SESSION,
  DELETE_CHAT_SESSION,
  SET_ACTIVE_SESSION,
  SET_CHAT_SESSIONS,
  CLEAR_CHAT_SESSIONS,
  FETCH_ANSWER_REQUEST,
  FETCH_ANSWER_SUCCESS,
  FETCH_ANSWER_FAILURE,
  SAVE_ANSWER
} from './types'
import { searchAPI } from '../services/api'
import { addMessage, clearMessages } from './messageActions'

// Generate a UUID v4
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Create a new chat session
export const createChatSession = (query = null) => (dispatch) => {
  const sessionId = generateUUID()
  const session = {
    id: sessionId,
    title: query || 'New Chat',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    messages: []
  }
  
  console.log('🆕 Creating new chat session with UUID:', sessionId)
  
  dispatch({
    type: CREATE_CHAT_SESSION,
    payload: session
  })
  
  dispatch(setActiveSession(sessionId))
  
  return sessionId
}

// Update chat session
export const updateChatSession = (sessionId, updates) => ({
  type: UPDATE_CHAT_SESSION,
  payload: { sessionId, updates }
})

// Delete chat session
export const deleteChatSession = (sessionId) => (dispatch) => {
  dispatch({
    type: DELETE_CHAT_SESSION,
    payload: sessionId
  })
  
  // Clear messages for this session
  dispatch(clearMessages(sessionId))
}

// Set active session
export const setActiveSession = (sessionId) => ({
  type: SET_ACTIVE_SESSION,
  payload: sessionId
})

// Set all chat sessions (for loading from storage)
export const setChatSessions = (sessions) => ({
  type: SET_CHAT_SESSIONS,
  payload: sessions
})

// Clear all chat sessions
export const clearChatSessions = () => ({
  type: CLEAR_CHAT_SESSIONS
})

// Fetch conversation messages from API
export const fetchConversationMessages = (sessionId) => async (dispatch, getState) => {
  dispatch({ type: FETCH_ANSWER_REQUEST })
  
  try {
    console.log('🔍 Fetching conversation messages for session:', sessionId)
    
    // Check if we already have messages for this session (from WebSocket)
    const state = getState()
    const existingMessages = state.messages?.messagesBySession?.[sessionId] || []
    const hasExistingMessages = existingMessages.length > 0
    
    if (hasExistingMessages) {
      console.log('⚠️ Session already has messages from WebSocket, skipping API call')
      dispatch({
        type: FETCH_ANSWER_SUCCESS,
        payload: { messages: [] }
      })
      return { messages: [] }
    }
    
    // Try conversations endpoint first, fallback to answers endpoint
    let response
    try {
      response = await searchAPI.getConversationMessages(sessionId)
      console.log('📦 Conversation messages response:', response)
    } catch (conversationError) {
      if (conversationError.response?.status === 404) {
        console.log('📦 Conversations endpoint not found, trying answers endpoint')
        try {
          response = await searchAPI.getAnswer(sessionId)
          console.log('📦 Answer response (fallback):', response)
          // Convert single answer to messages format
          if (response.question && response.answer_md) {
            response = {
              messages: [
                {
                  id: `${sessionId}-user`,
                  role: 'user',
                  content: response.question,
                  timestamp: response.created_at
                },
                {
                  id: `${sessionId}-assistant`,
                  role: 'assistant',
                  content: response.answer_md,
                  citations: response.citations,
                  timestamp: response.created_at
                }
              ]
            }
          } else {
            response = { messages: [] }
          }
        } catch (answerError) {
          if (answerError.response?.status === 404) {
            console.log('📦 Neither endpoint found session, returning empty (this is normal for new sessions)')
          } else {
            console.log('📦 Answer endpoint failed with non-404 error:', answerError.response?.status)
          }
          response = { messages: [] }
        }
      } else {
        throw conversationError
      }
    }
    
    dispatch({
      type: FETCH_ANSWER_SUCCESS,
      payload: response
    })
    
    // Clear existing messages for this session first
    dispatch(clearMessages(sessionId))
    
    // Add all messages from the conversation
    if (response.messages && Array.isArray(response.messages)) {
      console.log('➕ Adding', response.messages.length, 'messages from API')
      response.messages.forEach(message => {
        dispatch(addMessage({
          id: message.id,
          sessionId,
          role: message.role,
          content: message.content,
          citations: message.citations,
          timestamp: message.timestamp || message.created_at,
          status: 'received'
        }))
      })
    }
    
    // Update session title if we have messages
    if (response.messages && response.messages.length > 0) {
      const firstUserMessage = response.messages.find(m => m.role === 'user')
      if (firstUserMessage) {
        dispatch(updateChatSession(sessionId, {
          title: firstUserMessage.content.substring(0, 100) + (firstUserMessage.content.length > 100 ? '...' : ''),
          updatedAt: new Date().toISOString()
        }))
      }
    }
    
    return response
  } catch (error) {
    console.error('❌ Failed to fetch conversation messages:', error)
    dispatch({
      type: FETCH_ANSWER_FAILURE,
      payload: error.response?.data?.detail || error.message
    })
    // Don't throw error for 404s, just return empty
    if (error.response?.status === 404) {
      return { messages: [] }
    }
    throw error
  }
}

// Fetch answer from API (legacy - for backwards compatibility)
export const fetchAnswer = fetchConversationMessages

// Save answer (after research completes)
export const saveAnswer = (sessionId, answerId, question, answer, citations) => ({
  type: SAVE_ANSWER,
  payload: {
    sessionId,
    answerId,
    question,
    answer,
    citations,
    savedAt: new Date().toISOString()
  }
})

// Clean up duplicate sessions based on title and creation time
export const cleanupDuplicateSessions = () => (dispatch, getState) => {
  const state = getState()
  const sessions = state.chat?.sessions || {}
  const sessionArray = Object.values(sessions)
  
  const duplicateIds = []
  const seen = new Set()
  
  sessionArray.forEach(session => {
    const key = `${session.title}-${Math.floor(new Date(session.createdAt).getTime() / 60000)}` // Round to minute
    if (seen.has(key)) {
      duplicateIds.push(session.id)
      console.log('🧹 Marking duplicate session for removal:', session.title, session.id)
    } else {
      seen.add(key)
    }
  })
  
  // Remove duplicates
  duplicateIds.forEach(sessionId => {
    dispatch(deleteChatSession(sessionId))
  })
  
  console.log('✅ Cleaned up', duplicateIds.length, 'duplicate sessions')
}

// Load chat history (conversations)
export const loadChatHistory = () => async (dispatch, getState) => {
  try {
    console.log('🔍 Loading chat history from conversations endpoint')
    const response = await searchAPI.getConversations(1, 50)
    
    // Get existing sessions to avoid duplicates
    const state = getState()
    const existingSessions = state.chat?.sessions || {}
    
    if (response.conversations) {
      console.log('📦 Loaded', response.conversations.length, 'conversations')
      // Only create sessions for conversations that don't already exist
      response.conversations.forEach(conversation => {
        const sessionId = conversation.id
        
        // Skip if session already exists
        if (existingSessions[sessionId]) {
          console.log('⚠️ Session', sessionId, 'already exists, skipping')
          return
        }
        
        // Create session
        dispatch({
          type: CREATE_CHAT_SESSION,
          payload: {
            id: sessionId,
            title: conversation.title || 'Conversation',
            createdAt: conversation.created_at,
            updatedAt: conversation.updated_at,
            messageCount: conversation.message_count || 0
          }
        })
      })
    } else {
      // Fallback to old answers endpoint if conversations not available
      console.log('⚠️ Conversations endpoint not available, falling back to answers')
      const response = await searchAPI.getAnswers(1, 50)
      
      if (response.answers) {
        response.answers.forEach(answer => {
          const sessionId = answer.id
          
          // Skip if session already exists
          if (existingSessions[sessionId]) {
            console.log('⚠️ Session', sessionId, 'already exists, skipping')
            return
          }
          
          // Create session
          dispatch({
            type: CREATE_CHAT_SESSION,
            payload: {
              id: sessionId,
              title: answer.question,
              createdAt: answer.created_at,
              updatedAt: answer.created_at,
              answerId: answer.id
            }
          })
        })
      }
    }
    
    // Clean up any duplicates that might have been created
    dispatch(cleanupDuplicateSessions())
  } catch (error) {
    console.error('Failed to load chat history:', error)
  }
}

// Create a new conversation on the server
export const createServerConversation = (query) => async (dispatch) => {
  try {
    console.log('🆕 Creating new conversation on server:', query)
    
    const response = await searchAPI.createConversation(
      query || 'New Conversation',
      query // Use query as initial prompt if provided
    )
    
    console.log('✅ Server conversation created:', response.id)
    
    // Create local session
    const session = {
      id: response.id,
      title: response.title,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
      messages: [],
      messageCount: response.message_count || 0
    }
    
    dispatch({
      type: CREATE_CHAT_SESSION,
      payload: session
    })
    
    dispatch(setActiveSession(response.id))
    
    return response.id
  } catch (error) {
    console.error('Failed to create server conversation:', error)
    // Fallback to local-only session
    return dispatch(createChatSession(query))
  }
}

// Fetch chat history (alias for loadChatHistory)
export const fetchChatHistory = loadChatHistory